import {
    GetDPSDetailResponse,
    QuestionnaireConfig,
    QuestionnaireEvaluateRequestAnswer,
    QuestionnaireEvaluateRequestAnswers,
    QuestionnaireQuestion,
} from "@repo/api";
import { FundsRow, StepperType } from "@repo/logic";
import { useMemo } from "react";
import { useTypedTranslation } from "../../../hooks";
import { ProgressBarListRow } from "../../ProgressBarList";
import { EditInvestmentStrategyMode } from "../EditInvestmentStrategy/EditInvestmentStrategy.types";
import { linkAnswersWithQuestions } from "./useLinkAnswersWithApiQuestions";

export type QuestionnaireFormValues = {
    [questionId: string]: { [answerId: string]: boolean | string | null | undefined } | string | null | undefined;
};

// Transforms form values into API result format
export const transformDataToApiRequest = (
    data: QuestionnaireFormValues,
    questionsData: QuestionnaireQuestion[] | undefined,
    sustainabilityId: string,
    contractId: string,
) => {
    const linkedData = linkAnswersWithQuestions(questionsData)(data);
    const result: QuestionnaireEvaluateRequestAnswers = {
        sustainabilityId: sustainabilityId,
        isNewContract: false, //This is edit form of existing contract
        rejected: false, //In this form user decided to fill the form
        contractId: contractId,
        userAnswers: linkedData.map<QuestionnaireEvaluateRequestAnswer>((item) => ({
            questionId: item.questionId,
            answers: item.userAnswers.filter((a) => !!a.answer).map((answer) => answer.answerId),
        })),
    };
    return result;
};

export const mapCurrentAllocationsFromDetail = (
    data?: GetDPSDetailResponse,
    config?: QuestionnaireConfig,
    mode?: EditInvestmentStrategyMode,
) => {
    if (!mode) return undefined;

    return data?.funds && config
        ? {
              currentAllocations: data.funds.map((fund) => ({
                  fundId: config.funds.find((f) => f.code === fund.code)?.id || -1,
                  percentage: mode === "new" ? fund.depositRatio : fund.balanceRatio,
              })),
              savings: data.savedAmount,
          }
        : undefined;
};

export const fillLabelsToFundRows = (
    rows?: FundsRow[],
    config?: QuestionnaireConfig,
): ProgressBarListRow[] | undefined => {
    return rows && config
        ? rows.map(
              (r) =>
                  ({
                      id: r.id,
                      label: config.funds.find((f) => f.id === r.id)?.name ?? "",
                      value: r.value,
                  }) as ProgressBarListRow,
          )
        : undefined;
};

export const useRejectModalTexts = (stepperType: StepperType | undefined) => {
    const { t } = useTypedTranslation();

    return useMemo(() => {
        let result: {
            title: string;
            text: string;
            cancelButtonTitle: string;
            confirmButtonTitle: string;
        } = {
            title: t("investmentStrategy.pensionQuestionnaire.questionnairePage.rejectModal.title"),
            text: t("investmentStrategy.pensionQuestionnaire.questionnairePage.rejectModal.text"),
            cancelButtonTitle: t("investmentStrategy.pensionQuestionnaire.questionnairePage.rejectModal.cancel"),
            confirmButtonTitle: t("investmentStrategy.pensionQuestionnaire.questionnairePage.rejectModal.confirm"),
        };

        if (stepperType === StepperType.PensionQuestionnaire) {
            result.text = t(
                "investmentStrategy.pensionQuestionnaire.questionnairePage.rejectModal.textPensionQuestionnaire",
            );
            result.confirmButtonTitle = t(
                "investmentStrategy.pensionQuestionnaire.questionnairePage.rejectModal.confirmPensionQuestionnaire",
            );
        }

        if (stepperType === StepperType.ChangePensionContribution) {
            result.text = t(
                "investmentStrategy.pensionQuestionnaire.questionnairePage.rejectModal.textForChangeContribution",
            );
        }

        return result;
    }, [stepperType, t]);
};
